import { useMutation } from "@apollo/client";
import AttachFile from "@mui/icons-material/AttachFile";
import MailOutline from "@mui/icons-material/MailOutline";
import MonetizationOnOutlined from "@mui/icons-material/MonetizationOnOutlined";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Save from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { IssueListButton } from "components/Forms/IssueListButton";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { PaymentDialog } from "components/Payment/PaymentDialog";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { SubmissionFormProvider } from "contexts/SubmissionFormContext";
import {
  DocumentType,
  GetSubmissionQuery,
  Payee,
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionEncampStatus,
  SubmissionSource,
  SubmissionStatus,
  SubmissionType,
} from "generated-graphql/graphql";

type SubmissionQueryResult = NonNullable<GetSubmissionQuery["submission"]>;
type SubmissionDocEntry = NonNullable<SubmissionQueryResult["documents"]>[number];
type SubmissionReviewerEntry = SubmissionQueryResult["reviewers"][number];
type FacilitySubmissionEntry = SubmissionQueryResult["facilitySubmissions"][number];
import { extensionFromName, FullScreenDocumentPreview } from "components/Document/shared";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { SubmissionReviewerSigner, SubmitIntent, useSubmissionForm } from "hooks/useSubmissionForm";
import { useDeleteSubmission } from "hooks/useSubmissionMutations";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { ASSIGN_PEER_REVIEW_TO_ME_MUTATION } from "queries/submissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { hasCriticalIssues } from "util/forms";

import { shouldAdvanceWithoutReview } from "./advanceWithoutReview";
import { DocumentPreviewSection } from "./DocumentPreviewSection";
import { DocumentUploadSection, SubmissionDocument } from "./DocumentUploadSection";
import { EmbeddedDocumentPreparationUrlDialog } from "./EmbeddedSignature/EmbeddedDocumentPreparationUrlDialog";
import {
  partitionSubmissionDocuments,
  sortSubmissionDocumentsByType,
  type SubmissionDocumentMetadata,
} from "./helpers/documentHelpers";
import { buildFormReviewers, reconstructReviewersAfterRemoval } from "./helpers/reviewerHelpers";
import { RejectionRoutingDialog } from "./RejectionRoutingDialog";
import { ReviewerManagementDialog } from "./ReviewerManagementDialog";
import { Reviewer } from "./ReviewersEditList";
import { SaveAndPrepareForSigningButton } from "./SaveAndPrepareForSigningButton";
import { SignerDialog } from "./SignerDialog";
import { SubmissionCommentStream } from "./SubmissionCommentStream";
import { SubmissionFormFields } from "./SubmissionFormFields";
import { SubmissionPayment } from "./SubmissionPayment";
import { SubmissionRevisionBanner } from "./SubmissionRevisionBanner";
import { SubmissionStatusChipWithPopover } from "./SubmissionStatusChipWithPopover";
import { SwapSubmissionDocumentDialog } from "./SwapSubmissionDocumentDialog";
import { CreateSubmissionInfoTooltip, EditSubmissionInfoTooltip } from "./Tooltips";
import { UpdateSubmissionStatusButton } from "./UpdateSubmissionStatusButton";

interface SubmissionFormProps {
  onClose: () => void;
  onSubmit?: (submissionId: string) => void;
  submissionId?: string; // If provided, we're in edit mode
  isEditMode?: boolean;
  loading?: boolean;
  virtualTaskId?: string; // Virtual task ID for creating submissions from virtual events
  initialFormData?: {
    title?: string;
    facilityIds?: string[];
    dueDate?: any; // DateTime object
    submissionType?: SubmissionType;
    notes?: string;
    recipient?: string;
    submissionMethod?: any; // SubmissionMethod enum
  };
}

/**
 * Determines when the save button should be visible
 *
 * For non-staff users:
 * - Show only for NOT_STARTED and IN_PROGRESS (draft) statuses
 *
 * For staff users:
 * - Show for drafts (NOT_STARTED, IN_PROGRESS)
 * - Show for review stages (ENCAMP_INITIAL_REVIEW, IN_REVIEW)
 * - Show for processing (PROCESSING_SUBMISSION)
 * - Hide for completed submissions (FILING_COMPLETE, NOT_REPORTING)
 *
 * This allows staff to update metadata (like staff assignee) at any
 * stage without affecting the submission workflow.
 */
function showSaveButton(status?: SubmissionStatus | null, isStaff?: boolean) {
  // Always show for draft statuses
  if (!status || status === SubmissionStatus.NotStarted || status === SubmissionStatus.InProgress) {
    return true;
  }

  // For staff only: show save button for in-review and processing statuses
  if (isStaff) {
    return (
      status === SubmissionStatus.EncampInitialReview ||
      status === SubmissionStatus.InReview ||
      status === SubmissionStatus.ProcessingSubmission
    );
  }

  // Don't show for FILING_COMPLETE or NOT_REPORTING
  return false;
}

interface ShouldShowSaveButtonParams {
  status?: SubmissionStatus | null;
  isStaff?: boolean;
}

export function shouldShowSaveButton({ status, isStaff }: ShouldShowSaveButtonParams) {
  if (status === SubmissionStatus.FilingComplete) {
    // Filing complete submissions are read-only, but staff can still save document changes.
    // Keep the save button visible (disabled when no doc changes) to match other flows.
    return Boolean(isStaff);
  }

  return showSaveButton(status, isStaff);
}

export function shouldShowSwapDocumentAction({
  isStaff,
  isEditMode,
}: {
  isStaff: boolean;
  isEditMode: boolean;
}) {
  if (!isStaff || !isEditMode) {
    return false;
  }

  return true;
}

/**
 * Returns the appropriate label for the save button based on submission status
 */
function getSaveButtonLabel(status?: SubmissionStatus | null) {
  if (!status || status === SubmissionStatus.NotStarted || status === SubmissionStatus.InProgress) {
    return "SAVE WITHOUT STARTING";
  }
  return "SAVE CHANGES";
}

export function SubmissionForm({
  onClose,
  onSubmit,
  submissionId,
  isEditMode = false,
  loading = false,
  virtualTaskId,
  initialFormData,
}: SubmissionFormProps) {
  const theme = useTheme();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const alerts = useAlerts();
  const navigate = useNavigate();

  // Component state
  const [reviewerDialogOpen, setReviewerDialogOpen] = useState(false);
  const [signerDialogOpen, setSignerDialogOpen] = useState(false);
  const [confirmReviewerSignerDialogOpen, setConfirmReviewerSignerDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);
  const [rejectionRoutingDialogOpen, setRejectionRoutingDialogOpen] = useState(false);
  const [assignPeerReview, { loading: assigningPeerReview }] = useMutation(
    ASSIGN_PEER_REVIEW_TO_ME_MUTATION
  );
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);
  const [embeddedDocumentPreparationUrlDialogOpen, setEmbeddedDocumentPreparationUrlDialogOpen] =
    useState(false);
  const [documents, setDocuments] = useState<SubmissionDocumentMetadata[]>([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<SubmissionDocument[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const [signerToBeConfirmed, setSignerToBeConfirmed] = useState<SubmissionReviewerSigner | null>(
    null
  );
  const [reviewerToBeConfirmed, setReviewerToBeConfirmed] = useState<Reviewer[] | null>(null);
  const [confirmSignerReviewerDialogOpen, setConfirmSignerReviewerDialogOpen] = useState(false);
  const [moveReviewerDatesDialogOpen, setMoveReviewerDatesDialogOpen] = useState(false);
  const [pendingDueDateShift, setPendingDueDateShift] = useState<{ deltaDays: number } | null>(
    null
  );
  const [newSubmissionId, setNewSubmissionId] = useState<string | undefined>(undefined);
  const [attachmentPreviewDialogOpen, setAttachmentPreviewDialogOpen] = useState(false);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<SubmissionDocument | null>(null);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapTargetDocument, setSwapTargetDocument] = useState<SubmissionDocument | null>(null);

  const [deleteSubmission, { loading: deletingSubmission }] = useDeleteSubmission();

  const onSubmitCallback = useCallback(
    (payload: { submissionId: string; openPreparationDialog?: boolean }) => {
      const { submissionId, openPreparationDialog } = payload;
      if (openPreparationDialog) {
        setEmbeddedDocumentPreparationUrlDialogOpen(true);
        if (!isEditMode) {
          setNewSubmissionId(submissionId);
        }
      } else {
        onSubmit?.(submissionId);
      }
    },
    [isEditMode, onSubmit]
  );

  const submissionDocumentInputs = useMemo(
    () =>
      documents.map((d) => ({
        documentId: d.id,
        type: d.submissionDocumentType,
      })),
    [documents]
  );
  const attachmentDocumentInputs = useMemo(
    () =>
      additionalDocuments.map((d) => ({
        documentId: d.id,
        type: d.submissionDocumentType,
      })),
    [additionalDocuments]
  );

  const {
    form,
    reviewers,
    setReviewers,
    signer,
    setSigner,
    staffAssignee,
    setStaffAssignee,
    handleFormSubmit,
    saveDocumentsOnly,
    loading: formLoading,
    submissionDocuments,
    submissionPayments,
    submissionData,
    refetchSubmission,
    needsDocumentPreparation,
    comments,
    setComments,
    hasInitializedForm,
    isFormReady,
    issues,
    errors,
    isValidating,
    dueDateWarningsByUserId,
  } = useSubmissionForm({
    submissionId,
    isEditMode,
    onSubmit: onSubmitCallback,
    submissionDocuments: submissionDocumentInputs,
    attachmentDocuments: attachmentDocumentInputs,
    virtualTaskId,
    initialFormData,
  });
  const { control, handleSubmit, watch, setValue, formState } = form;
  const { isDirty, isSubmitting } = formState;
  const reviewRequired = watch("reviewRequired");
  const signatureType = watch("signatureType");
  const reviewMode = watch("reviewMode");
  const peerReviewRequired = watch("peerReviewRequired");
  const advancesWithoutReview = shouldAdvanceWithoutReview({
    reviewerCount: reviewers.length,
    hasSigner: Boolean(signer),
    peerReviewRequired,
    reviewRequired,
  });

  // Hide all issues for completed submissions - we don't care about compliance errors
  const displayIssues = useMemo(() => {
    if (
      submissionData?.submission?.status === SubmissionStatus.FilingComplete ||
      submissionData?.submission?.status === SubmissionStatus.NotReporting
    ) {
      return [];
    }
    return issues ?? [];
  }, [submissionData?.submission?.status, issues]);

  const isFilingComplete = submissionData?.submission?.status === SubmissionStatus.FilingComplete;
  const isRejected = submissionData?.submission?.encampStatus === SubmissionEncampStatus.Rejected;
  const isFormDisabled = isFilingComplete || isRejected;

  const [isSavingDocumentsOnly, setIsSavingDocumentsOnly] = useState(false);

  const title = watch("title");
  const submissionType = watch("submissionType");
  const facilityIds = watch("facilityIds");
  const dueDate = watch("dueDate");

  // We don't want users editing the submission type if it was created from the
  // task system. This will keep those in sync
  const canEditSubmissionType = useMemo(() => {
    return !isEditMode || submissionData?.submission?.source !== SubmissionSource.System;
  }, [isEditMode, submissionData]);

  // Disable facility editing for submissions created from requirements (SYSTEM source)
  // On create: lock when virtualTaskId is set (creating from a requirement).
  // On edit: lock when submission.source is SYSTEM.
  const canEditFacilities = useMemo(() => {
    if (!isEditMode) {
      return !virtualTaskId;
    }
    return submissionData?.submission?.source !== SubmissionSource.System;
  }, [isEditMode, virtualTaskId, submissionData]);

  // Only capture initial state once submission data is loaded
  const initialDocuments = useMemo(() => {
    if (isEditMode && submissionData?.submission && submissionDocuments.length > 0) {
      return submissionDocuments
        .filter(
          (subDoc: SubmissionDocEntry) =>
            subDoc.type === SubmissionDocumentType.Submission ||
            subDoc.type === SubmissionDocumentType.RejectedSubmission ||
            subDoc.type === SubmissionDocumentType.SignedSubmission
        )
        .map((subDoc: SubmissionDocEntry) => subDoc.document.id)
        .sort();
    }
    return [];
  }, [isEditMode, submissionData?.submission, submissionDocuments]);

  const initialAdditionalDocuments = useMemo(() => {
    if (isEditMode && submissionData?.submission && submissionDocuments.length > 0) {
      return submissionDocuments
        .filter((subDoc: SubmissionDocEntry) => subDoc.type === SubmissionDocumentType.Attachment)
        .map((subDoc: SubmissionDocEntry) => subDoc.document.id)
        .sort();
    }
    return [];
  }, [isEditMode, submissionData?.submission, submissionDocuments]);

  const initialReviewers = useMemo(() => {
    if (isEditMode && submissionData?.submission?.reviewers) {
      return submissionData.submission.reviewers
        .filter((r: SubmissionReviewerEntry) => !r.isSigner)
        .map((r: SubmissionReviewerEntry) => r.user.id)
        .sort();
    }
    return [];
  }, [isEditMode, submissionData]);

  const initialReviewerStatuses = useMemo(() => {
    if (isEditMode && submissionData?.submission?.reviewers) {
      return submissionData.submission.reviewers
        .filter((r: SubmissionReviewerEntry) => !r.isSigner)
        .map((r: SubmissionReviewerEntry) => ({
          id: r.user.id,
          reviewerStatus: r.reviewerStatus ?? null,
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
    }
    return [];
  }, [isEditMode, submissionData]);

  const initialSignerEmail = useMemo(() => {
    if (isEditMode && submissionData?.submission?.reviewers) {
      const signerReviewer = submissionData.submission.reviewers.find(
        (r: SubmissionReviewerEntry) => r.isSigner
      );
      return signerReviewer?.user?.email ?? null;
    }
    return null;
  }, [isEditMode, submissionData]);

  const initialSignerOverrides = useMemo(() => {
    if (isEditMode && submissionData?.submission?.reviewers) {
      const signerReviewer = submissionData.submission.reviewers.find(
        (r: SubmissionReviewerEntry) => r.isSigner
      );
      if (!signerReviewer) return null;
      return {
        signerNameOverride: signerReviewer.signerNameOverride ?? null,
        signerEmailOverride: signerReviewer.signerEmailOverride ?? null,
      };
    }
    return null;
  }, [isEditMode, submissionData]);

  const initialStaffAssignee = useMemo(() => {
    if (isEditMode && submissionData?.submission?.staffAssignee) {
      return submissionData.submission.staffAssignee.id;
    }
    return null;
  }, [isEditMode, submissionData]);

  const initialFacilityIds = useMemo(() => {
    if (isEditMode && submissionData?.submission?.facilitySubmissions) {
      return submissionData.submission.facilitySubmissions
        .map((f: FacilitySubmissionEntry) => f.facility.id)
        .sort();
    }
    return [];
  }, [isEditMode, submissionData]);

  // Check for unsaved document changes (similar to payment form pattern)
  const hasUnsavedDocumentChanges = useMemo(() => {
    if (!isEditMode || !isFormReady) {
      return false;
    }

    const currentDocumentIds = documents.map((d) => d.id).sort();
    const documentsDirty = JSON.stringify(currentDocumentIds) !== JSON.stringify(initialDocuments);

    const currentAdditionalDocumentIds = additionalDocuments.map((d) => d.id).sort();
    const additionalDocumentsDirty =
      JSON.stringify(currentAdditionalDocumentIds) !== JSON.stringify(initialAdditionalDocuments);

    return documentsDirty || additionalDocumentsDirty;
  }, [
    isEditMode,
    isFormReady,
    documents,
    additionalDocuments,
    initialDocuments,
    initialAdditionalDocuments,
  ]);

  const hasUnsavedReviewerChanges = useMemo(() => {
    if (!isEditMode || !isFormReady) {
      return false;
    }

    const currentReviewerIds = reviewers.map((r) => r.id).sort();
    const reviewersDirty = JSON.stringify(currentReviewerIds) !== JSON.stringify(initialReviewers);

    const currentReviewerStatuses = reviewers
      .map((r) => ({ id: r.id, reviewerStatus: r.reviewerStatus ?? null }))
      .sort((a, b) => a.id.localeCompare(b.id));
    const reviewerStatusDirty =
      JSON.stringify(currentReviewerStatuses) !== JSON.stringify(initialReviewerStatuses);

    const currentSignerEmail = signer?.email ?? null;
    const signerIdentityDirty = currentSignerEmail !== initialSignerEmail;

    const currentOverrides = signer
      ? {
          signerNameOverride: signer.signerNameOverride ?? null,
          signerEmailOverride: signer.signerEmailOverride ?? null,
        }
      : null;
    const signerOverridesDirty =
      JSON.stringify(currentOverrides) !== JSON.stringify(initialSignerOverrides);

    const currentStaffAssigneeId = staffAssignee?.id || null;
    const staffAssigneeDirty = currentStaffAssigneeId !== initialStaffAssignee;

    return (
      reviewersDirty ||
      reviewerStatusDirty ||
      signerIdentityDirty ||
      signerOverridesDirty ||
      staffAssigneeDirty
    );
  }, [
    isEditMode,
    isFormReady,
    reviewers,
    signer,
    staffAssignee,
    initialReviewers,
    initialReviewerStatuses,
    initialSignerEmail,
    initialSignerOverrides,
    initialStaffAssignee,
  ]);

  const hasUnsavedFacilityChanges = useMemo(() => {
    if (!isEditMode || !isFormReady) {
      return false;
    }

    const currentFacilityIds = [...(facilityIds || [])].sort();
    return JSON.stringify(currentFacilityIds) !== JSON.stringify(initialFacilityIds);
  }, [isEditMode, isFormReady, facilityIds, initialFacilityIds]);

  // Comprehensive dirty state check
  const isFormDirty = useMemo(() => {
    // Don't check dirty state until form is ready
    if (!isFormReady) {
      return false;
    }

    if (!isEditMode) {
      return (
        isDirty ||
        documents.length > 0 ||
        additionalDocuments.length > 0 ||
        reviewers.length > 0 ||
        signer !== null ||
        staffAssignee !== null
      );
    }

    return (
      isDirty || hasUnsavedDocumentChanges || hasUnsavedReviewerChanges || hasUnsavedFacilityChanges
    );
  }, [
    isFormReady,
    isEditMode,
    isDirty,
    documents,
    additionalDocuments,
    reviewers,
    signer,
    staffAssignee,
    hasUnsavedDocumentChanges,
    hasUnsavedReviewerChanges,
    hasUnsavedFacilityChanges,
  ]);

  const [selectedDocument, setSelectedDocument] = useState<SubmissionDocument | null>(null);
  const [addEditDocumentDialogState, setAddEditDocumentDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);
  const [currentDocumentType, setCurrentDocumentType] = useState<SubmissionDocumentType>(
    SubmissionDocumentType.Submission
  );

  const handleDocumentSelect = useCallback(
    async (document: SubmissionDocument) => {
      setSelectedDocument(document);

      if (document.url && document.url.startsWith("blob:")) {
        setPreviewUrl(document.url);
        return;
      }

      if (document.id) {
        try {
          const { data } = await getDocumentDownloadLink({
            variables: { id: document.id },
          });

          if (data?.getDocumentDownloadLink) {
            setPreviewUrl(data.getDocumentDownloadLink);
          } else {
            setPreviewUrl(null);
          }
        } catch (error) {
          setPreviewUrl(null);
        }
      }
    },
    [getDocumentDownloadLink]
  );

  const handleAttachmentPreview = useCallback(
    async (document: SubmissionDocument) => {
      setSelectedAttachment(document);

      if (document.url && document.url.startsWith("blob:")) {
        setAttachmentPreviewUrl(document.url);
        setAttachmentPreviewDialogOpen(true);
        return;
      }

      if (document.id) {
        try {
          const { data } = await getDocumentDownloadLink({
            variables: { id: document.id },
          });

          if (data?.getDocumentDownloadLink) {
            setAttachmentPreviewUrl(data.getDocumentDownloadLink);
            setAttachmentPreviewDialogOpen(true);
          } else {
            setAttachmentPreviewUrl(null);
          }
        } catch (error) {
          setAttachmentPreviewUrl(null);
        }
      }
    },
    [getDocumentDownloadLink]
  );
  useEffect(() => {
    if (isEditMode && submissionDocuments.length > 0) {
      // Map submission documents to internal format
      const docs = submissionDocuments.map((subDoc: SubmissionDocEntry) => ({
        id: subDoc.document.id,
        submissionDocumentId: subDoc.id,
        name: subDoc.document.title || "Untitled",
        documentType: subDoc.document.documentType || DocumentType.Other,
        submissionDocumentType: subDoc.type,
        url: subDoc.document.storageLink,
        fileExtension: subDoc.document.fileExtension,
      }));

      // Partition into submissions and attachments
      const { submissionDocuments: submissionDocs, attachmentDocuments: attachmentDocs } =
        partitionSubmissionDocuments(docs);

      // Sort submission documents by type priority
      const sortedSubmissionDocs = sortSubmissionDocumentsByType(submissionDocs);

      setDocuments(sortedSubmissionDocs);
      setAdditionalDocuments(attachmentDocs);

      if (sortedSubmissionDocs.length > 0) {
        handleDocumentSelect(sortedSubmissionDocs[0]);
      }
    }
  }, [isEditMode, submissionDocuments, handleDocumentSelect]);

  const handleAddDocument = (type: SubmissionDocumentType) => {
    setCurrentDocumentType(type);
    setAddEditDocumentDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
    });
  };

  const handleDeleteDocument = (documentId: string, type: SubmissionDocumentType) => {
    if (
      type === SubmissionDocumentType.Submission ||
      type === SubmissionDocumentType.SignedSubmission ||
      type === SubmissionDocumentType.RejectedSubmission
    ) {
      const wasSelectedDocument = selectedDocument?.id === documentId;

      setDocuments((prev) => {
        const filtered = prev.filter((doc) => doc.id !== documentId);

        if (wasSelectedDocument && filtered.length > 0) {
          handleDocumentSelect(filtered[0]);
        } else if (wasSelectedDocument) {
          setSelectedDocument(null);
          setPreviewUrl(null);
        }

        return filtered;
      });
    } else {
      setAdditionalDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    }
  };

  const handleDocumentSubmit = async (document: any) => {
    const newDocument: SubmissionDocument = {
      id: document.id || Date.now().toString(),
      name: document.title || "Untitled",
      documentType: document.documentType || DocumentType.Other,
      submissionDocumentType: currentDocumentType,
      url: document.storageLink || document.fileUrl,
      fileExtension: extensionFromName(document.title),
    };

    if (
      currentDocumentType === SubmissionDocumentType.Submission ||
      currentDocumentType === SubmissionDocumentType.SignedSubmission ||
      currentDocumentType === SubmissionDocumentType.RejectedSubmission
    ) {
      setDocuments((prev) => {
        const updated = [...prev, newDocument];
        handleDocumentSelect(newDocument);
        return updated;
      });
    } else {
      setAdditionalDocuments((prev) => [...prev, newDocument]);
    }
  };

  const handleOpenSwapDialog = useCallback(
    (document: SubmissionDocument) => {
      if (!document.submissionDocumentId) {
        alerts.error("Unable to swap document: missing submission document ID");
        return;
      }

      setSwapTargetDocument(document);
      setSwapDialogOpen(true);
    },
    [alerts]
  );

  const handleSignerClick = () => {
    if (signatureType !== SignatureType.None) {
      setSignerDialogOpen(true);
    } else {
      setSignerDialogOpen(false);
    }
  };

  const handleSaveWithoutStarting = useCallback(() => {
    if (!title?.trim() || !submissionType || !dueDate) {
      return;
    }

    // Use react-hook-form submission flow so `isSubmitting` is true while saving.
    // This prevents the UnfinishedChangesPrompt from triggering during navigation.
    handleSubmit((data) => handleFormSubmit(data))();
  }, [title, submissionType, dueDate, handleSubmit, handleFormSubmit]);

  const handleSave = useCallback(async () => {
    if (!isFilingComplete) {
      handleSaveWithoutStarting();
      return;
    }

    setIsSavingDocumentsOnly(true);
    try {
      await saveDocumentsOnly();
    } finally {
      setIsSavingDocumentsOnly(false);
    }
  }, [isFilingComplete, handleSaveWithoutStarting, saveDocumentsOnly]);

  // TODO - can we get rid of these proxy callbacks?
  const handleMoveToReview = useCallback(() => {
    handleSubmit((data) => handleFormSubmit(data, SubmitIntent.MOVE_TO_REVIEW))();
  }, [handleSubmit, handleFormSubmit]);

  const handlePrepareForSigning = useCallback(() => {
    handleSubmit((data) => handleFormSubmit(data, SubmitIntent.PREPARE_FOR_SIGNING))();
  }, [handleSubmit, handleFormSubmit]);

  const handlePreparationDialogClose = useCallback(async () => {
    setEmbeddedDocumentPreparationUrlDialogOpen(false);

    if (newSubmissionId || submissionId) {
      if (newSubmissionId) {
        navigate(`/o/${tenantId}/waste/submissions/${newSubmissionId}/edit`);
      } else {
        await refetchSubmission({ id: submissionId });
      }
    }
  }, [newSubmissionId, submissionId, navigate, tenantId, refetchSubmission]);

  const handleRemoveReviewer = (reviewerId: string) => {
    setReviewers((prev) => reconstructReviewersAfterRemoval(prev, reviewerId));
  };

  const handleOpenPayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setPaymentDialogOpen(true);
  };

  const handleAddPayment = () => {
    setSelectedPaymentId(undefined);
    setPaymentDialogOpen(true);
  };

  const handleDeleteSubmission = async () => {
    if (!submissionId) return;

    try {
      await deleteSubmission({
        variables: { id: submissionId },
      });
      alerts.success("Submission deleted successfully");
      onClose();
    } catch (error) {
      alerts.error("Failed to delete submission", error);
    }
  };

  const updateFormReviewers = useCallback(
    (currentSigner: SubmissionReviewerSigner | null, newReviewers: Reviewer[]) => {
      const formReviewers = buildFormReviewers(newReviewers, currentSigner);

      setValue("reviewers", formReviewers, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  // When the due date moves, offer to shift each non-signer reviewer's due date by the same
  // amount, preserving the buffer. The date itself always updates; the dialog only governs
  // whether the reviewer rows move. Signers (held in separate `signer` state) are untouched.
  const handleDueDateChange = useCallback(
    (newDueDate: DateTime | null) => {
      const oldDueDate = dueDate;
      setValue("dueDate", newDueDate ?? undefined, { shouldDirty: true, shouldValidate: true });

      if (!newDueDate?.isValid || !oldDueDate?.isValid) return;
      // Whole calendar days, not milliseconds: a DST transition inside the range
      // makes a millisecond delta ±1h off a whole day, which shifts reviewer
      // dates to 23:00 the previous day and persists them a day early.
      const deltaDays = Math.round(newDueDate.diff(oldDueDate, "days").days);
      if (deltaDays === 0) return;

      if (!reviewers.some((r) => r.dueDate?.isValid)) return;

      setPendingDueDateShift({ deltaDays });
      setMoveReviewerDatesDialogOpen(true);
    },
    [dueDate, setValue, reviewers]
  );

  const closeMoveReviewerDatesDialog = useCallback(() => {
    setMoveReviewerDatesDialogOpen(false);
    setPendingDueDateShift(null);
  }, []);

  const handleConfirmMoveReviewerDates = useCallback(() => {
    if (!pendingDueDateShift) {
      closeMoveReviewerDatesDialog();
      return;
    }
    const { deltaDays } = pendingDueDateShift;
    const shiftedReviewers = reviewers.map((r) =>
      r.dueDate?.isValid ? { ...r, dueDate: r.dueDate.plus({ days: deltaDays }) } : r
    );
    setReviewers(shiftedReviewers);
    updateFormReviewers(signer, shiftedReviewers);
    closeMoveReviewerDatesDialog();
  }, [
    pendingDueDateShift,
    reviewers,
    setReviewers,
    updateFormReviewers,
    signer,
    closeMoveReviewerDatesDialog,
  ]);

  const submissionDocumentIssues = useMemo(() => {
    return displayIssues.filter((i) => i.key === "documents");
  }, [displayIssues]);

  const draftValidationIssues = useMemo(() => {
    return displayIssues.filter(
      (i) => i.key !== "documents" && i.modelName !== "SubmissionDocument"
    );
  }, [displayIssues]);

  const bothSaveDisabled = useMemo(() => {
    return (
      loading ||
      formLoading ||
      isSubmitting ||
      (!isEditMode && !isFormDirty) ||
      isValidating ||
      (isEditMode && !isFormReady)
    );
  }, [loading, formLoading, isSubmitting, isEditMode, isFormDirty, isValidating, isFormReady]);

  const sendToReviewersDisabled = useMemo(() => {
    const baseDisabled = bothSaveDisabled || displayIssues.length > 0;

    if (signatureType === SignatureType.Digital) {
      return baseDisabled || needsDocumentPreparation;
    }

    return baseDisabled;
  }, [bothSaveDisabled, displayIssues, needsDocumentPreparation, signatureType]);

  const showAssignPeerReviewBanner = useMemo(() => {
    if (!isStaff || !isEditMode || !submissionId) return false;
    const status = submissionData?.submission?.status;
    if (status !== SubmissionStatus.EncampInitialReview) return false;
    const submissionReviewers = submissionData?.submission?.reviewers ?? [];
    const hasNoStaffReviewer = !submissionReviewers.some((r: SubmissionReviewerEntry) => r.isStaff);
    if (!hasNoStaffReviewer) return false;
    return submissionData?.submission?.peerReviewRequired === true;
  }, [
    isStaff,
    isEditMode,
    submissionId,
    submissionData?.submission?.status,
    submissionData?.submission?.reviewers,
    submissionData?.submission?.peerReviewRequired,
  ]);

  const showUpdateSubmissionStatusButton = useMemo(() => {
    if (signatureType !== SignatureType.Digital) {
      return true;
    }

    return !needsDocumentPreparation;
  }, [signatureType, needsDocumentPreparation]);

  const hasPendingChangesForMarkComplete = useMemo(() => {
    return isFormDirty;
  }, [isFormDirty]);

  const draftSaveDisabled = useMemo(() => {
    return (
      hasCriticalIssues(draftValidationIssues ?? []) ||
      (!isFormDirty && isEditMode) ||
      bothSaveDisabled ||
      !!newSubmissionId
    );
  }, [draftValidationIssues, bothSaveDisabled, newSubmissionId, isFormDirty, isEditMode]);

  const shouldRenderSaveButton = useMemo(() => {
    return shouldShowSaveButton({
      status: submissionData?.submission?.status,
      isStaff,
    });
  }, [submissionData?.submission?.status, isStaff]);

  // Memoized state for document upload permissions
  const hasSubmissionOrSignedDocument = useMemo(() => {
    return documents.some(
      (doc) =>
        doc.submissionDocumentType === SubmissionDocumentType.Submission ||
        doc.submissionDocumentType === SubmissionDocumentType.SignedSubmission
    );
  }, [documents]);

  const isWetInkSignatureProcessing = useMemo(() => {
    return (
      isEditMode &&
      isStaff &&
      submissionData?.submission?.signatureType === SignatureType.WetInk &&
      submissionData?.submission?.status === SubmissionStatus.ProcessingSubmission
    );
  }, [isEditMode, isStaff, submissionData]);

  const canAddSubmissionDocument = useMemo(() => {
    return !hasSubmissionOrSignedDocument;
  }, [hasSubmissionOrSignedDocument]);

  const canAddSignedSubmissionDocument = useMemo(() => {
    return isWetInkSignatureProcessing;
  }, [isWetInkSignatureProcessing]);

  const canSwapSubmissionDocument = useMemo(() => {
    return shouldShowSwapDocumentAction({
      isStaff,
      isEditMode,
    });
  }, [isStaff, isEditMode]);

  // The swap mutation triggers a submission refetch, which resets the form to server
  // state and overwrites any unsaved signer/reviewer edits. Block the swap action while
  // the form is dirty so the user must commit or discard pending changes first.
  const swapDocumentDisabledTooltip = useMemo(() => {
    return isFormDirty ? "Save changes before swapping a document." : undefined;
  }, [isFormDirty]);

  // Whether swapping the targeted document will invalidate an existing signature. Suppressed
  // post-completion: a swap on a Filing-Complete submission (the documented rename workaround)
  // has no stuck state, so the re-sign acknowledgment would be misleading. Non-digital
  // signatures have no BoldSign envelope to invalidate, and the re-sign copy is digital-only.
  const swapWillInvalidateSignature = useMemo(() => {
    if (isFilingComplete || signatureType !== SignatureType.Digital) {
      return false;
    }
    const targetExternalDocumentId = submissionData?.submission?.documents?.find(
      (d) => d.id === swapTargetDocument?.submissionDocumentId
    )?.externalDocumentId;
    const anySignerSigned = (submissionData?.submission?.reviewers ?? []).some(
      (r) =>
        r.isSigner &&
        (r.signatureStatus === SignatureStatus.Sent ||
          r.signatureStatus === SignatureStatus.Completed)
    );
    return Boolean(targetExternalDocumentId) || anySignerSigned;
  }, [
    isFilingComplete,
    signatureType,
    submissionData?.submission?.documents,
    submissionData?.submission?.reviewers,
    swapTargetDocument?.submissionDocumentId,
  ]);

  const handleViewSubmission = useCallback(() => {
    if (submissionId && tenantId) {
      navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
    }
  }, [navigate, submissionId, tenantId]);

  return (
    <SubmissionFormProvider
      issues={displayIssues}
      dueDateWarningsByUserId={dueDateWarningsByUserId}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper elevation={0}>
          <Box sx={{ px: 4, pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h5">
                  {isEditMode ? "Edit Submission" : "Create Submission"}
                  <InfoIconTooltip
                    tooltip={
                      isEditMode ? <EditSubmissionInfoTooltip /> : <CreateSubmissionInfoTooltip />
                    }
                    fontSize="24px"
                    iconButtonSx={{ ml: 1, mb: 0.5 }}
                  />
                </Typography>
                {isEditMode && submissionData?.submission?.status && (
                  <SubmissionStatusChipWithPopover
                    submissionId={submissionId}
                    submissionStatus={submissionData.submission.status}
                    encampStatus={submissionData.submission.encampStatus}
                    isStaff={isStaff}
                  />
                )}
              </Stack>

              {isStaff && isEditMode && submissionId && (
                <Button
                  variant="contained"
                  color="staff"
                  startIcon={<Visibility />}
                  onClick={handleViewSubmission}
                >
                  View Submission
                </Button>
              )}
            </Stack>
          </Box>

          {isEditMode &&
            isStaff &&
            submissionData?.submission?.encampStatus === SubmissionEncampStatus.Rejected && (
              <Alert
                severity="warning"
                sx={{ mx: 4, mb: 2 }}
                action={
                  submissionId ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setRejectionRoutingDialogOpen(true)}
                    >
                      Resolve
                    </Button>
                  ) : undefined
                }
              >
                This submission has been <strong>rejected</strong> by a reviewer. Use rejection
                routing to determine next steps.
              </Alert>
            )}

          {showAssignPeerReviewBanner && (
            <Alert
              severity="info"
              icon={<PersonAdd />}
              sx={{ mx: 4, mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={assigningPeerReview}
                  onClick={async () => {
                    if (!submissionId) return;
                    try {
                      await assignPeerReview({
                        variables: { submissionId },
                        refetchQueries: ["GetSubmission"],
                      });
                      alerts.success("You have been assigned as the peer reviewer.");
                    } catch (e) {
                      alerts.error(
                        e instanceof Error ? e.message : "Failed to assign peer review."
                      );
                    }
                  }}
                >
                  {assigningPeerReview ? "Assigning..." : "Assign to Me"}
                </Button>
              }
            >
              This submission is in <strong>Initial Review</strong> with no peer reviewer assigned.
            </Alert>
          )}

          {isEditMode && submissionData?.submission && (
            <SubmissionRevisionBanner
              tenantId={tenantId ?? ""}
              revisedFromSubmission={submissionData.submission.revisedFromSubmission}
              revisionSubmissions={submissionData.submission.revisionSubmissions}
              sx={{ mx: 4, mb: 2 }}
            />
          )}

          {/* This is a reminder for staff to mail the submission document to the signer. */}
          {isWetInkSignatureProcessing && (
            <Box sx={{ px: 4, pb: 2 }}>
              <Alert severity="info" icon={<MailOutline />}>
                <Typography fontWeight="bold" marginBottom={0.5}>
                  Ready to mail for wet ink signature
                </Typography>
                <Typography variant="body2">
                  This submission has been reviewed. Encamp staff will send the document for a
                  wet-ink signature and update the status once it has been submitted to the
                  regulatory agency.
                </Typography>
              </Alert>
            </Box>
          )}

          <form onSubmit={(e) => e.preventDefault()}>
            <Box
              sx={{
                overflow: "auto",
                px: 4,
                pt: 1,
                pb: 2,
              }}
            >
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Submission Information
                  </Typography>
                  <Stack spacing={3}>
                    <SubmissionFormFields
                      control={control}
                      reviewRequired={reviewRequired}
                      signatureType={signatureType}
                      disabled={isFormDisabled}
                      isStaff={isStaff}
                      onReviewerClick={() => setReviewerDialogOpen(true)}
                      onSignerClick={handleSignerClick}
                      setValue={setValue}
                      setReviewers={setReviewers}
                      reviewers={reviewers}
                      reviewMode={reviewMode}
                      signer={signer}
                      setSigner={setSigner}
                      staffAssignee={staffAssignee}
                      setStaffAssignee={setStaffAssignee}
                      submissionDueDate={dueDate}
                      onDueDateChange={handleDueDateChange}
                      canEditSubmissionType={canEditSubmissionType}
                      canEditFacilities={canEditFacilities}
                      submissionId={submissionId}
                      submissionEncampStatus={submissionData?.submission?.encampStatus}
                    />

                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Notes
                      </Typography>
                    </Box>

                    <Box>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            variant="outlined"
                            label="Notes"
                            placeholder="Notes"
                            disabled={isFormDisabled}
                          />
                        )}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ mb: 2, fontWeight: 400 }}>
                        Attachments
                      </Typography>
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Submission Document *
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {canAddSubmissionDocument && (
                            <IconButton
                              size="small"
                              sx={{ color: "primary.main" }}
                              onClick={() => handleAddDocument(SubmissionDocumentType.Submission)}
                            >
                              <AttachFile fontSize="small" />
                            </IconButton>
                          )}
                          {canAddSignedSubmissionDocument && (
                            <IconButton
                              size="small"
                              color="staff"
                              onClick={() =>
                                handleAddDocument(SubmissionDocumentType.SignedSubmission)
                              }
                            >
                              <AttachFile fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Box>
                        {submissionDocumentIssues.length > 0 && (
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {submissionDocumentIssues.map((i, index) => (
                              <div key={index}>{i.message}</div>
                            ))}
                          </Alert>
                        )}
                      </Box>
                      {documents.length > 0 && (
                        <DocumentUploadSection
                          documents={documents}
                          onDeleteDocument={(docId) =>
                            handleDeleteDocument(docId, SubmissionDocumentType.Submission)
                          }
                          onSelectDocument={
                            isEditMode
                              ? async (doc) => {
                                  await handleDocumentSelect(doc);
                                  setFullScreenPreviewOpen(true);
                                }
                              : handleDocumentSelect
                          }
                          issues={displayIssues}
                          tenantId={tenantId}
                          canSwapDocument={canSwapSubmissionDocument}
                          swapDocumentDisabledTooltip={swapDocumentDisabledTooltip}
                          onSwapDocument={handleOpenSwapDialog}
                        />
                      )}
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Additional Attachment(s)
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{ color: "primary.main" }}
                          onClick={() => handleAddDocument(SubmissionDocumentType.Attachment)}
                          disabled={isRejected}
                        >
                          <AttachFile fontSize="small" />
                        </IconButton>
                      </Box>
                      {additionalDocuments.length > 0 && (
                        <DocumentUploadSection
                          documents={additionalDocuments}
                          onDeleteDocument={(docId) =>
                            handleDeleteDocument(docId, SubmissionDocumentType.Attachment)
                          }
                          onSelectDocument={handleAttachmentPreview}
                          tenantId={tenantId}
                        />
                      )}
                    </Box>

                    {isEditMode && (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Payment(s)
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{ color: "primary.main" }}
                            onClick={handleAddPayment}
                          >
                            <MonetizationOnOutlined fontSize="small" />
                          </IconButton>
                        </Box>

                        {submissionPayments.length > 0 && (
                          <SubmissionPayment
                            submissionPayments={submissionPayments}
                            isEditMode={isEditMode}
                            onAddPayment={handleAddPayment}
                            onOpenPayment={handleOpenPayment}
                          />
                        )}
                      </Box>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Submission Preview</Typography>
                    {selectedDocument && previewUrl && (
                      <IconButton
                        size="small"
                        onClick={() => setFullScreenPreviewOpen(true)}
                        sx={{ ml: 0.5 }}
                        aria-label="Open full-screen preview"
                      >
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ mt: -1 }}>
                    <DocumentPreviewSection
                      selectedDocument={selectedDocument}
                      previewUrl={previewUrl}
                      fileExtension={selectedDocument?.fileExtension ?? undefined}
                    />
                    {previewUrl && (
                      <FullScreenDocumentPreview
                        open={fullScreenPreviewOpen}
                        onClose={() => setFullScreenPreviewOpen(false)}
                        fileUrl={previewUrl}
                        fileExtension={selectedDocument?.fileExtension ?? undefined}
                        title={selectedDocument?.name ?? "Document Preview"}
                        sidePanel={
                          <SubmissionCommentStream
                            submissionId={submissionId}
                            comments={comments}
                            commentAdded={(comment) => {
                              setComments((prev) => [...prev, comment]);
                            }}
                            commentDeleted={(commentId) => {
                              setComments((prev) => prev.filter((c) => c.id !== commentId));
                            }}
                          />
                        }
                      />
                    )}

                    <Box sx={{ mt: 3 }}>
                      <SubmissionCommentStream
                        submissionId={submissionId}
                        comments={comments}
                        commentAdded={(comment) => {
                          setComments((prev) => [...prev, comment]);
                        }}
                        commentDeleted={(commentId) => {
                          setComments((prev) => prev.filter((comment) => comment.id !== commentId));
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                px: 4,
                py: 2.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={onClose}
                  variant="text"
                  sx={{ color: theme.palette.primary.light }}
                >
                  CANCEL
                </Button>
                {isEditMode &&
                  submissionId &&
                  isStaff &&
                  (() => {
                    const hasLinkedRevisions =
                      (submissionData?.submission?.revisionSubmissions?.length ?? 0) > 0;
                    const deleteButton = (
                      <Button
                        onClick={() => setConfirmDeleteDialogOpen(true)}
                        variant="text"
                        disabled={
                          deletingSubmission ||
                          loading ||
                          formLoading ||
                          isRejected ||
                          hasLinkedRevisions
                        }
                        sx={{ color: theme.palette.error.main }}
                      >
                        DELETE Submission
                      </Button>
                    );
                    if (hasLinkedRevisions) {
                      return (
                        <Tooltip title="This submission has linked revision submissions. Delete the revisions first.">
                          <span>{deleteButton}</span>
                        </Tooltip>
                      );
                    }
                    return deleteButton;
                  })()}
              </Stack>

              <Stack direction="row" spacing={2}>
                {submissionData?.submission?.status !== SubmissionStatus.FilingComplete &&
                  submissionData?.submission?.status !== SubmissionStatus.NotReporting && (
                    <IssueListButton issues={displayIssues} />
                  )}
                {shouldRenderSaveButton && (
                  <Button
                    variant="outlined"
                    disabled={
                      draftSaveDisabled ||
                      isSavingDocumentsOnly ||
                      isRejected ||
                      (isFilingComplete && !hasUnsavedDocumentChanges)
                    }
                    startIcon={<Save />}
                    onClick={handleSave}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {getSaveButtonLabel(submissionData?.submission?.status)}
                  </Button>
                )}
                {signatureType === SignatureType.Digital && needsDocumentPreparation && (
                  <SaveAndPrepareForSigningButton
                    signer={signer}
                    issues={displayIssues}
                    documents={documents}
                    loading={loading}
                    formLoading={formLoading}
                    isSubmitting={isSubmitting}
                    isValidating={isValidating}
                    onClick={handlePrepareForSigning}
                  />
                )}

                {showUpdateSubmissionStatusButton && tenantId && !isRejected && (
                  <UpdateSubmissionStatusButton
                    submissionId={submissionId}
                    currentStatus={submissionData?.submission?.status}
                    isStaff={isStaff}
                    isEditMode={isEditMode}
                    isSendToReviewersDisabled={sendToReviewersDisabled}
                    loading={loading}
                    formLoading={formLoading}
                    onMoveToReview={handleMoveToReview}
                    advancesWithoutReview={advancesWithoutReview}
                    onSuccess={() => {
                      refetchSubmission();
                    }}
                    statusUpdatesDisabled={isFormDirty}
                    markFilingCompleteDisabledTooltip={
                      isFormDirty
                        ? "The form has pending changes, please save in order to mark as filing complete"
                        : undefined
                    }
                  />
                )}
              </Stack>
            </Box>
          </form>
        </Paper>

        {reviewerDialogOpen && (
          <ReviewerManagementDialog
            open={reviewerDialogOpen}
            onClose={() => setReviewerDialogOpen(false)}
            onChange={(newReviewers, newReviewMode) => {
              setReviewers(newReviewers);
              setValue("reviewMode", newReviewMode, {
                shouldDirty: true,
                shouldValidate: true,
              });
              updateFormReviewers(signer, newReviewers);
            }}
            onSave={(newReviewers, newReviewMode) => {
              // Check if any reviewer is already the signer
              const conflictingReviewers = newReviewers.filter((r) => r.id === signer?.id);

              if (conflictingReviewers.length > 0) {
                setReviewerToBeConfirmed(newReviewers);
                setConfirmSignerReviewerDialogOpen(true);
              } else {
                setReviewers(newReviewers);
                setValue("reviewMode", newReviewMode, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                updateFormReviewers(signer, newReviewers);
                setReviewerDialogOpen(false);
              }
            }}
            initialReviewers={reviewers}
            initialReviewMode={reviewMode}
            submissionFacilityIds={facilityIds}
          />
        )}

        {addEditDocumentDialogState.open && (
          <AddEditDocumentDialog
            dialogState={addEditDocumentDialogState}
            setDialogState={setAddEditDocumentDialogState}
            tenantId={tenantId || ""}
            showFacilityPicker={false}
            refetchQueries={[]}
            onSubmit={handleDocumentSubmit}
          />
        )}

        {swapDialogOpen &&
          swapTargetDocument &&
          swapTargetDocument.submissionDocumentId &&
          submissionId &&
          tenantId && (
            <SwapSubmissionDocumentDialog
              open={swapDialogOpen}
              onClose={() => {
                setSwapDialogOpen(false);
                setSwapTargetDocument(null);
              }}
              submissionId={submissionId}
              submissionDocumentId={swapTargetDocument.submissionDocumentId}
              tenantId={tenantId}
              currentDocumentName={swapTargetDocument.name}
              currentDocumentType={swapTargetDocument.documentType}
              signatureWillBeInvalidated={swapWillInvalidateSignature}
              onSuccess={async () => {
                await refetchSubmission({ id: submissionId });
              }}
            />
          )}

        {signerDialogOpen && (
          <SignerDialog
            open={signerDialogOpen}
            onClose={() => setSignerDialogOpen(false)}
            onChange={(signerData) => {
              setSigner(signerData);
              updateFormReviewers(signerData, reviewers);
            }}
            onSave={(signerData) => {
              if (reviewers.some((r) => r.id === signerData.id)) {
                setSignerToBeConfirmed(signerData);
                setConfirmReviewerSignerDialogOpen(true);
              } else {
                setSigner(signerData);
                updateFormReviewers(signerData, reviewers);
                setSignerDialogOpen(false);
              }
            }}
            initialSigner={signer}
            signatureType={signatureType}
            submissionFacilityIds={facilityIds}
          />
        )}

        {confirmReviewerSignerDialogOpen && (
          <ConfirmDialog
            open={confirmReviewerSignerDialogOpen}
            onClose={() => setConfirmReviewerSignerDialogOpen(false)}
            onConfirm={() => {
              handleRemoveReviewer(signerToBeConfirmed?.id || "");
              setSigner(signerToBeConfirmed);
              updateFormReviewers(signerToBeConfirmed, reviewers);
              setSignerToBeConfirmed(null);
              setConfirmReviewerSignerDialogOpen(false);
              setSignerDialogOpen(false);
            }}
            header="Are you sure you want to add this person as a signatory and remove them as a reviewer?"
            info={
              <Typography component="span">
                This person is already assigned to review this document, confirm if you would like
                to remove them as a reviewer and instead add them as a signatory.
              </Typography>
            }
            confirmText="Yes, add as signatory"
            cancelText="No, keep as reviewer"
          />
        )}

        {confirmSignerReviewerDialogOpen && (
          <ConfirmDialog
            open={confirmSignerReviewerDialogOpen}
            onClose={() => setConfirmSignerReviewerDialogOpen(false)}
            onConfirm={() => {
              setSigner(null);
              setReviewers(reviewerToBeConfirmed || []);
              updateFormReviewers(null, reviewerToBeConfirmed || []);
              setReviewerToBeConfirmed(null);
              setConfirmSignerReviewerDialogOpen(false);
              setReviewerDialogOpen(false);
            }}
            header="Are you sure you want to add this person as a reviewer and remove them as a signatory?"
            info="This person is currently set as the signatory for this submission."
            confirmText="Yes, add as reviewer"
            cancelText="No, keep as signatory"
          />
        )}

        {moveReviewerDatesDialogOpen && pendingDueDateShift && (
          <ConfirmDialog
            open={moveReviewerDatesDialogOpen}
            onClose={closeMoveReviewerDatesDialog}
            onConfirm={handleConfirmMoveReviewerDates}
            header="Move reviewer due dates?"
            confirmText="Move reviewer dates"
            cancelText="Keep as-is"
          >
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              You changed the submission due date. Do you want to also shift reviewer due dates the
              same number of days? Signer due dates won't change.
            </Typography>
            <Stack spacing={1}>
              {reviewers
                .filter((r) => r.dueDate?.isValid)
                .map((r) => (
                  <Box key={r.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {r.name || r.email || "Reviewer"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.dueDate?.toFormat("MM/dd/yyyy")} →{" "}
                      {r.dueDate
                        ?.plus({ days: pendingDueDateShift.deltaDays })
                        .toFormat("MM/dd/yyyy")}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </ConfirmDialog>
        )}

        {confirmDeleteDialogOpen && (
          <ConfirmDialog
            open={confirmDeleteDialogOpen}
            onClose={() => setConfirmDeleteDialogOpen(false)}
            onConfirm={() => {
              handleDeleteSubmission();
              setConfirmDeleteDialogOpen(false);
            }}
            header="Are you sure you want to delete this submission?"
            info="This action cannot be undone"
            confirmText="YES, DELETE"
            cancelText="CANCEL"
            minWidth="440px"
            maxWidth="440px"
          />
        )}

        {paymentDialogOpen && (
          <PaymentDialog
            open={paymentDialogOpen}
            onClose={() => {
              setPaymentDialogOpen(false);
              setSelectedPaymentId(undefined);
            }}
            tenantId={tenantId}
            associatedSubmissionIds={submissionId ? [submissionId] : []}
            paymentId={selectedPaymentId}
            defaultPayeeType={Payee.Rcra}
            onCompleted={async () => {
              if (submissionId && refetchSubmission) {
                await refetchSubmission();
              }
              setPaymentDialogOpen(false);
              setSelectedPaymentId(undefined);
            }}
          />
        )}

        {rejectionRoutingDialogOpen && submissionId && (
          <RejectionRoutingDialog
            open={rejectionRoutingDialogOpen}
            onClose={() => setRejectionRoutingDialogOpen(false)}
            submissionId={submissionId}
          />
        )}

        {embeddedDocumentPreparationUrlDialogOpen && (
          <EmbeddedDocumentPreparationUrlDialog
            open={embeddedDocumentPreparationUrlDialogOpen}
            onClose={() => {
              handlePreparationDialogClose();
            }}
            submissionId={newSubmissionId ?? submissionId ?? ""}
          />
        )}
        {attachmentPreviewUrl && (
          <FullScreenDocumentPreview
            open={attachmentPreviewDialogOpen}
            onClose={() => setAttachmentPreviewDialogOpen(false)}
            fileUrl={attachmentPreviewUrl}
            fileExtension={selectedAttachment?.fileExtension ?? undefined}
            title={selectedAttachment?.name ?? "Attachment Preview"}
            sidePanel={
              <SubmissionCommentStream
                submissionId={submissionId}
                comments={comments}
                commentAdded={(comment) => {
                  setComments((prev) => [...prev, comment]);
                }}
                commentDeleted={(commentId) => {
                  setComments((prev) => prev.filter((c) => c.id !== commentId));
                }}
              />
            }
          />
        )}

        <UnfinishedChangesPrompt
          when={
            isFormDirty &&
            !isSubmitting &&
            !isSavingDocumentsOnly &&
            !formLoading &&
            !deletingSubmission &&
            !embeddedDocumentPreparationUrlDialogOpen
          }
          header="Close without saving?"
          confirmText="Yes, close without saving"
          info="You have unsaved information. Going back without saving will clear the form."
          minWidth="440px"
          maxWidth="440px"
        />
      </Box>
    </SubmissionFormProvider>
  );
}
